import React from 'react';
import { Icon, getRouteStatus } from './LogisticsUtils';

const STATUS_THEME = {
  pending: {
    shell: 'border-amber-200/90 bg-gradient-to-br from-white via-amber-50/60 to-white',
    glow: 'from-amber-400/20 via-amber-300/10 to-transparent',
    pill: 'bg-amber-500 text-white',
    progress: 'bg-amber-500',
    accent: 'bg-amber-400'
  },
  in_progress: {
    shell: 'border-blue-200/90 bg-gradient-to-br from-white via-sky-50/70 to-white',
    glow: 'from-sky-400/20 via-blue-300/10 to-transparent',
    pill: 'bg-blue-600 text-white',
    progress: 'bg-blue-600',
    accent: 'bg-blue-500'
  },
  completed: {
    shell: 'border-emerald-200/90 bg-gradient-to-br from-white via-emerald-50/70 to-white',
    glow: 'from-emerald-400/20 via-emerald-300/10 to-transparent',
    pill: 'bg-emerald-600 text-white',
    progress: 'bg-emerald-600',
    accent: 'bg-emerald-500'
  },
  programmed: {
    shell: 'border-violet-200/90 bg-gradient-to-br from-white via-violet-50/70 to-white',
    glow: 'from-violet-400/20 via-fuchsia-300/10 to-transparent',
    pill: 'bg-violet-600 text-white',
    progress: 'bg-violet-600',
    accent: 'bg-violet-500'
  },
  cancelled: {
    shell: 'border-rose-200/90 bg-gradient-to-br from-white via-rose-50/70 to-white',
    glow: 'from-rose-400/20 via-red-300/10 to-transparent',
    pill: 'bg-rose-600 text-white',
    progress: 'bg-rose-600',
    accent: 'bg-rose-500'
  }
};

const TRANSPORT_ICONS = {
  camioneta:
    'M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25',
  moto:
    'M15.75 10.5l4.724-4.724A3.375 3.375 0 0015.75 3h-2.25a3.375 3.375 0 00-3.375 3.375v2.25M9.375 10.5H5.25A2.25 2.25 0 003 12.75v4.248M9.375 10.5h-.75V8.25m-.75 0a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm9 0a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM7.5 15.75m-3 0a3 3 0 116 0 3 3 0 01-6 0zm-3 0h7.5',
  auto:
    'M3.75 16.5v-1.125c0-.621.504-1.125 1.125-1.125h14.25c.621 0 1.125.504 1.125 1.125V16.5M4.5 12l1.32-3.301A2.25 2.25 0 017.909 7.5h8.182a2.25 2.25 0 012.089 1.199L19.5 12M6.75 16.5h.008v.008H6.75V16.5zm10.5 0h.008v.008h-.008V16.5z',
  camion:
    'M1.5 13.5h12m0 0V6.75A2.25 2.25 0 0011.25 4.5h-6A2.25 2.25 0 003 6.75v6.75m10.5 0h3.879a1.5 1.5 0 001.06-.44l1.621-1.62a1.5 1.5 0 00.44-1.061V9.75a1.5 1.5 0 00-.44-1.06l-1.62-1.621a1.5 1.5 0 00-1.061-.44H13.5m-9 10.5a1.5 1.5 0 103 0 1.5 1.5 0 00-3 0zm12 0a1.5 1.5 0 103 0 1.5 1.5 0 00-3 0z'
};

const TRANSPORT_LABELS = {
  camioneta: 'Camioneta',
  moto: 'Moto',
  auto: 'Auto',
  camion: 'Camión'
};

const RouteCard = ({ route, onSelect, onDelete, userRole, isHighlighted = false }) => {
  const destinations = Array.isArray(route?.destinations) ? route.destinations : [];
  const status = getRouteStatus(route);
  const completed = destinations.filter((d) => d.status === 'completed').length;
  const totalForProgress = destinations.filter(
    (d) => d.status !== 'cancelled' && d.status !== 'incomplete'
  ).length;
  const progress = totalForProgress > 0 ? (completed / totalForProgress) * 100 : 0;

  let statusKey = 'pending';
  let statusText = 'Pendiente';

  if (status === 'in_progress') {
    statusKey = 'in_progress';
    statusText = 'En progreso';
  } else if (status === 'completed') {
    statusKey = 'completed';
    statusText = 'Completada';
  } else if (status === 'programmed') {
    statusKey = 'programmed';
    statusText = 'Programada';
  } else if (status === 'cancelled') {
    statusKey = 'cancelled';
    statusText = 'Cancelada';
  }

  const theme = STATUS_THEME[statusKey];

  const transportKey = route?.transport || route?.transportType;
  const transportLabel = TRANSPORT_LABELS[transportKey] || 'Vehículo';

  const cardClass = [
    'group relative overflow-hidden rounded-[28px] border shadow-[0_18px_40px_-28px_rgba(15,23,42,0.55)]',
    'transition-all duration-300 cursor-pointer hover:-translate-y-1 hover:shadow-[0_24px_56px_-24px_rgba(15,23,42,0.30)]',
    theme.shell,
    isHighlighted ? 'ring-2 ring-violet-400 ring-offset-2 scale-[1.01]' : ''
  ].join(' ');

  const formattedDate = route?.date
    ? new Date(`${route.date}T00:00:00`).toLocaleDateString('es-AR', {
        weekday: 'short',
        day: 'numeric',
        month: 'short'
      })
    : 'Sin fecha';

  return (
    <div onClick={() => onSelect(route)} className={cardClass}>
      <div className={`absolute inset-x-0 top-0 h-1 ${theme.accent}`} />
      <div
        className={`pointer-events-none absolute -right-10 top-0 h-28 w-28 rounded-full bg-gradient-to-br ${theme.glow} blur-2xl`}
      />

      {userRole === 'admin' && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(e, route.id);
          }}
          className="absolute right-4 top-4 z-10 rounded-full border border-red-200/80 bg-white/90 p-2 text-red-600 opacity-0 shadow-sm backdrop-blur transition-all hover:bg-red-50 group-hover:opacity-100"
          title="Eliminar Ruta"
        >
          <Icon
            path="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
            className="h-4.5 w-4.5"
          />
        </button>
      )}

      <div className="relative flex h-full flex-col gap-4 px-5 py-5">
        <div className="flex items-start gap-3 pr-10">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[18px] bg-slate-900 text-white shadow-sm">
            <Icon
              path={TRANSPORT_ICONS[transportKey] || TRANSPORT_ICONS.camioneta}
              className="h-6 w-6"
            />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${theme.pill}`}
              >
                {statusText}
              </span>

              <span className="rounded-full border border-slate-200 bg-white/80 px-2.5 py-1 text-[11px] font-medium text-slate-500 backdrop-blur">
                {transportLabel}
              </span>
            </div>

            <h3 className="mt-3 truncate pr-2 text-[16px] font-bold tracking-tight text-slate-900">
              {route?.name || 'Ruta sin nombre'}
            </h3>

            <p className="mt-1 text-sm font-medium text-slate-600">
              {route?.transporterName || 'Sin transportista asignado'}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-600">
          <span className="rounded-full border border-slate-200 bg-white/80 px-2.5 py-1 font-medium backdrop-blur">
            {formattedDate}
          </span>

          <span className="rounded-full border border-slate-200 bg-white/80 px-2.5 py-1 font-medium backdrop-blur">
            {destinations.length} {destinations.length === 1 ? 'destino' : 'destinos'}
          </span>

          <span className="rounded-full border border-slate-200 bg-white/80 px-2.5 py-1 font-medium backdrop-blur">
            {completed}/{totalForProgress || destinations.length || 0} completados
          </span>
        </div>

        {route?.status === 'cancelled' && route?.cancelReason && (
          <div className="rounded-2xl border border-rose-200/80 bg-rose-50/80 px-3 py-2 text-[11px] text-rose-700">
            <span className="font-semibold uppercase tracking-[0.12em]">Motivo:</span>{' '}
            {route.cancelReason}
          </div>
        )}

        <div className="rounded-[22px] border border-slate-200/80 bg-white/70 p-3 backdrop-blur">
          <div className="flex items-center justify-between text-[11px] font-medium text-slate-500">
            <span>Progreso operativo</span>
            <span>{Math.round(progress)}%</span>
          </div>

          <div className="mt-2 h-2 rounded-full bg-slate-200/80">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${theme.progress}`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RouteCard;
